import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useLocation } from "react-router-dom"
import { createSelector } from "@reduxjs/toolkit"
import ActionButton from "Components/atoms/buttons/action-button"
import AreYouSureButtons from "Components/molecule/are-you-sure-buttons"
import Modal from "Components/molecule/modal"
import SelectInput from "Components/molecule/inputs/select-input"
import { SCREENS } from "models/screen"
import { DemoDataMode, DemoDataResult, DemoDataStatus } from "models/demo-data"
import { PermissionsSliceState } from "slices/auth/permissions/reducer"
import { DemoDataSliceState, setDemoDataStatus } from "slices/admin/demo-data/reducer"
import { createDemoData, getDemoDataStatus } from "slices/admin/demo-data/thunk"

const selectPermissions = createSelector(
    (state: any) => state.Permissions,
    ({ screens }: PermissionsSliceState) => ({ screens })
)

const selectDemoData = createSelector(
    (state: any) => state.DemoData,
    ({ status, loaded }: DemoDataSliceState) => ({ status, loaded })
)

const COUNT_OPTIONS = ["5", "10", "25", "50"].map(v => ({ value: v, label: v }))

// Several headers can mount at once; share one status request between them.
let statusRequest: Promise<DemoDataStatus | null> | null = null

const formatCounts = (counts: Record<string, number>) =>
    Object.entries(counts).map(([table, n]) => `${table}: ${n}`).join(", ")

/**
 * "Create Demo" button shown in the page header (Breadcrumb). It renders only for users with the
 * Admin screen (SC_AD_AD_008), only when the API runs against a dev database, and only on pages
 * whose screen has a demo module on the API (documentation/features/demo-data.md).
 */
const DemoDataButton = () => {
    const dispatch = useDispatch() as any
    const { pathname } = useLocation()
    const { screens } = useSelector(selectPermissions)
    const { status, loaded } = useSelector(selectDemoData)

    const [isOpen, setIsOpen] = useState(false)
    const [mode, setMode] = useState<DemoDataMode>('fake')
    const [count, setCount] = useState("10")
    const [processing, setProcessing] = useState(false)
    const [result, setResult] = useState<DemoDataResult | null>(null)

    const isAdmin = useMemo(() => screens.some(s => s.code === SCREENS.ADMIN), [screens])

    useEffect(() => {
        if (!isAdmin || loaded) return
        statusRequest = statusRequest ?? getDemoDataStatus()
        statusRequest.then(res => dispatch(setDemoDataStatus(res)))
    }, [isAdmin, loaded, dispatch])

    // The screen of this page is the one with the longest url that prefixes the path
    // (gate screens use "/" and are ignored).
    const module = useMemo(() => {
        if (!status?.enabled) return null
        const screen = screens
            .filter(s => s.url && s.url !== '/' && (pathname === s.url || pathname.startsWith(s.url + '/')))
            .sort((a, b) => b.url.length - a.url.length)[0]
        if (!screen) return null
        return status.modules.find(m => m.screens.includes(screen.code)) ?? null
    }, [status, screens, pathname])

    if (!isAdmin || !module) return null

    const canCopyPrd = Boolean(status?.prdCopyAvailable && module.supportsPrdCopy)
    const modeOptions = [
        { value: 'fake', label: 'Fake data (last 3 months)' },
        { value: 'prd', label: 'Copy from PRD (last month, anonymized)', disabled: !canCopyPrd },
    ]

    const close = () => {
        if (processing) return
        setIsOpen(false)
        setResult(null)
    }

    const handleCreate = async () => {
        setProcessing(true)
        const res = await createDemoData(module.key, mode, Number(count))
        setProcessing(false)
        if (res) setResult(res)
    }

    return <>
        <ActionButton onClick={() => setIsOpen(true)} title="Admin only - dev database">
            Create Demo <i className="mdi mdi-database-plus-outline" />
        </ActionButton>

        <Modal
            isOpen={isOpen}
            onClose={close}
            title={`Create demo data: ${module.label}`}
            subtitle="Dev database only. Records are added; nothing is deleted."
        >
            {result ? <>
                <p className="mb-1"><strong>Created:</strong> {formatCounts(result.created) || "nothing new"}</p>
                {Object.keys(result.parentsAdded).length > 0 &&
                    <p className="mb-1"><strong>Related records added:</strong> {formatCounts(result.parentsAdded)}</p>}
                {result.skipped > 0 &&
                    <p className="mb-1"><strong>Skipped:</strong> {result.skipped} (already in dev or missing required data)</p>}
                <AreYouSureButtons
                    className="d-flex gap-2 mt-3"
                    handleAccept={() => window.location.reload()}
                    handleDecline={close}
                    acceptText="Reload page"
                    declineText="Close"
                />
            </> : <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                    <SelectInput
                        name="demoMode"
                        label="Source"
                        options={modeOptions}
                        value={mode}
                        onChange={e => setMode(e.target.value as DemoDataMode)}
                    />
                    <SelectInput
                        name="demoCount"
                        label={mode === 'prd' ? "Max records to copy" : "Records to create"}
                        options={COUNT_OPTIONS}
                        value={count}
                        onChange={e => setCount(e.target.value)}
                    />
                    {!canCopyPrd &&
                        <small className="text-muted">
                            {module.supportsPrdCopy
                                ? "Copy from PRD is not configured on this API."
                                : `${module.label} only supports fake data.`}
                        </small>}
                </div>
                <AreYouSureButtons
                    disable={processing}
                    handleAccept={handleCreate}
                    handleDecline={close}
                    acceptText={processing ? "Creating..." : "Create"}
                    declineText="Cancel"
                />
            </>}
        </Modal>
    </>
}

export default DemoDataButton
